import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdClose, MdPayments, MdInbox } from 'react-icons/md';
import ReasonModal from '../components/ReasonModal';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDateTime, formatINR, statusBadgeClass } from '../utils/format';

const TABS = ['requested', 'processing', 'paid', 'rejected', 'all'];

export default function Withdrawals() {
  const [tab, setTab] = useState('requested');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasons, setReasons] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination(20);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [detail, setDetail] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [utr, setUtr] = useState('');
  const [rejectFor, setRejectFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      const params = { page, limit };
      if (tab !== 'all') params.status = tab;
      const res = await api.get('/admin/withdrawals', { params });
      setList(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch {
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, [tab, page, limit]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    api.get('/admin/contributors/meta/rejection-reasons')
      .then((res) => setReasons(res.data.data?.withdrawal || []))
      .catch(() => {});
  }, []);

  const act = async (id, action, body) => {
    setBusy(true);
    try {
      await api.patch(`/admin/withdrawals/${id}/${action}`, body || {});
      toast.success('Updated');
      setDetail(null); setPayModal(null); setUtr(''); setRejectFor(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally { setBusy(false); }
  };

  let payout = {};
  try { payout = typeof detail?.payout_snapshot === 'string' ? JSON.parse(detail.payout_snapshot) : (detail?.payout_snapshot || {}); } catch(e) {}

  return (
    <div className="page">
      <div className="page-header"><h2>Withdrawals</h2></div>

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : list.length === 0 ? (
        <div className="empty-state">
          <MdInbox className="empty-icon" />
          <h3>Nothing here</h3>
          <p>No “{tab}” withdrawals.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Contributor</th><th>Finance Details</th><th>Amount</th><th>Requested</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {list.map((w) => {
                let payout = {};
                try { payout = typeof w.payout_snapshot === 'string' ? JSON.parse(w.payout_snapshot) : (w.payout_snapshot || {}); } catch(e) {}
                return (
                <tr key={w.id}>
                  <td>{w.contributor?.name || '-'}<div className="text-muted" style={{ fontSize: 12 }}>{w.contributor?.email}</div></td>
                  <td>
                    <div style={{ fontSize: 13, lineHeight: '1.4' }}>
                      {payout.method === 'bank' && (
                        <>
                          <div><strong>Bank:</strong> {payout.bank_account_name}</div>
                          <div className="text-muted">{payout.bank_account_number} ({payout.bank_ifsc})</div>
                        </>
                      )}
                      {payout.method === 'upi' && (
                        <div><strong>UPI:</strong> <span className="text-muted">{payout.upi_id}</span></div>
                      )}
                      <div style={{ marginTop: 4 }}>
                        <strong>PAN:</strong> <span className="text-muted">{payout.pan_number || '-'}</span>
                      </div>
                      <div>
                        <strong>Aadhaar:</strong> <span className="text-muted">{payout.aadhaar_number || '-'}</span>
                      </div>
                    </div>
                  </td>
                  <td><strong>{formatINR(w.amount)}</strong></td>
                  <td>{formatDateTime(w.created_at || w.createdAt)}</td>
                  <td><span className={`badge ${statusBadgeClass(w.status)}`}>{w.status}</span></td>
                  <td><button className="btn btn-sm btn-ghost" onClick={() => setDetail(w)}>Manage</button></td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
      )}

      {!loading && list.length > 0 && (
        <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
      )}

      {/* Manage modal */}
      {detail && (
        <div className="modal-overlay" onClick={() => setDetail(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{formatINR(detail.amount)} <span className={`badge ${statusBadgeClass(detail.status)}`}>{detail.status}</span></h3>
              <button className="modal-close" onClick={() => setDetail(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <div className="detail-row"><span className="detail-label">Contributor</span><span>{detail.contributor?.name} ({detail.contributor?.phone})</span></div>
              <div className="detail-row"><span className="detail-label">Requested</span><span>{formatDateTime(detail.created_at || detail.createdAt)}</span></div>

              <div className="card" style={{ marginTop: 12, background: 'var(--bg)' }}>
                <h4 style={{ marginBottom: 10 }}>Payout / KYC</h4>
                <div className="detail-row"><span className="detail-label">Method</span><span>{payout.method || '-'}</span></div>
                {payout.method === 'bank' && (
                  <>
                    <div className="detail-row"><span className="detail-label">Account name</span><span>{payout.bank_account_name || '-'}</span></div>
                    <div className="detail-row"><span className="detail-label">Account no.</span><span>{payout.bank_account_number || '-'}</span></div>
                    <div className="detail-row"><span className="detail-label">IFSC</span><span>{payout.bank_ifsc || '-'}</span></div>
                  </>
                )}
                {payout.method === 'upi' && (
                  <div className="detail-row"><span className="detail-label">UPI ID</span><span>{payout.upi_id || '-'}</span></div>
                )}
                <div className="detail-row"><span className="detail-label">Aadhaar</span><span>{payout.aadhaar_number || '-'}</span></div>
                <div className="detail-row"><span className="detail-label">PAN</span><span>{payout.pan_number || '-'}</span></div>
              </div>

              {detail.payout_reference && (
                <div className="detail-row" style={{ marginTop: 12 }}><span className="detail-label">Payout ref / UTR</span><span>{detail.payout_reference}</span></div>
              )}
              {detail.status === 'rejected' && detail.rejection_reason && (
                <div className="detail-row"><span className="detail-label">Rejection reason</span><span>{detail.rejection_reason}</span></div>
              )}
            </div>

            <div className="modal-footer">
              {detail.status === 'requested' && (
                <button className="btn btn-ghost" disabled={busy} onClick={() => act(detail.id, 'processing')}>Mark Processing</button>
              )}
              {(detail.status === 'requested' || detail.status === 'processing') && (
                <>
                  <button className="btn btn-danger" disabled={busy} onClick={() => setRejectFor(detail)}>Reject</button>
                  <button className="btn btn-primary" disabled={busy} onClick={() => { setPayModal(detail); setUtr(''); }}>
                    <MdPayments /> Mark Paid
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mark paid → UTR */}
      {payModal && (
        <div className="modal-overlay" onClick={() => setPayModal(null)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Mark as Paid</h3>
              <button className="modal-close" onClick={() => setPayModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p className="text-muted" style={{ marginBottom: 16 }}>
                Confirm you've transferred {formatINR(payModal.amount)} offline. This writes a debit to the contributor's ledger.
              </p>
              <div className="form-group">
                <label>Payout reference / UTR (optional)</label>
                <input value={utr} placeholder="e.g. UTR123456789" onChange={(e) => setUtr(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPayModal(null)} disabled={busy}>Cancel</button>
              <button className="btn btn-primary" onClick={() => act(payModal.id, 'paid', { payout_reference: utr })} disabled={busy}>
                {busy ? 'Saving…' : 'Confirm Paid'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectFor && (
        <ReasonModal
          title="Reject Withdrawal"
          reasons={reasons}
          confirmLabel="Reject Withdrawal"
          busy={busy}
          onCancel={() => setRejectFor(null)}
          onConfirm={(reason) => act(rejectFor.id, 'reject', { reason })}
        />
      )}
    </div>
  );
}
